import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import { Button, Spinner, StatusSelect } from '../../components/ds';
import PlacementCode from '../../components/PlacementCode/PlacementCode';
import AdReport from '../../components/AdReport/AdReport';
import './AdDetail.css';

const STATUS_OPTIONS = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '執行中' },
  { value: 'paused', label: '已暫停' },
  { value: 'archived', label: '封存' },
];

export default function AdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activating, setActivating] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    fetchAd();
  }, [id]);

  const fetchAd = async () => {
    setLoading(true);
    try {
      const data = await api.getPortalAd(id);
      setAd(data);
    } catch (err) {
      setError(err.message || 'Failed to load ad');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm('確定要啟用此廣告？啟用後所有 PAC 資源將變為 active 狀態。')) return;
    setActivating(true);
    try {
      const result = await api.activatePortalAd(id);
      if (result.errors && result.errors.length > 0) {
        addToast('部分資源啟用失敗：' + result.errors.join('; '), 'error');
      } else {
        addToast('廣告已啟用！', 'success');
      }
      await fetchAd();
    } catch (err) {
      addToast('啟用失敗：' + (err.message || '未知錯誤'), 'error');
    } finally {
      setActivating(false);
    }
  };

  const handlePause = async () => {
    try {
      await api.pausePortalAd(id);
      addToast('廣告已暫停', 'success');
      fetchAd();
    } catch (err) {
      addToast('暫停失敗：' + err.message, 'error');
    }
  };

  const handleResume = async () => {
    try {
      await api.resumePortalAd(id);
      addToast('廣告已恢復投放', 'success');
      fetchAd();
    } catch (err) {
      addToast('恢復失敗：' + err.message, 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  if (error || !ad) {
    return (
      <div className="ad-detail-error">
        <p>{error || 'Ad not found'}</p>
        <Button variant="secondary" onClick={() => navigate('/ads')}>返回列表</Button>
      </div>
    );
  }

  const targeting = ad.targeting && typeof ad.targeting === 'object' ? ad.targeting : {};

  return (
    <div>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <a href="#" onClick={(e) => { e.preventDefault(); navigate('/ads'); }}>All Ads</a>
        <span className="breadcrumb__sep">/</span>
        <span className="breadcrumb__current">{ad.name || `Ad #${ad.id}`}</span>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1>{ad.name || `Ad #${ad.id}`}</h1>
          <StatusSelect value={ad.status} options={STATUS_OPTIONS} disabled />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={() => navigate(`/ads/create?edit=${ad.id}&step=${ad.current_step || 1}`)}>編輯</Button>
          {ad.creative_uuid ? (
            <Button variant="secondary" onClick={() => navigate(`/ads/${ad.id}/creative`)}>查看素材</Button>
          ) : (
            <Button variant="secondary" onClick={() => navigate(`/ads/${ad.id}/creative`)}>建立素材</Button>
          )}
          {ad.status === 'draft' && ad.creative_uuid && (
            <Button onClick={handleActivate} disabled={activating}>
              {activating ? '啟用中...' : '啟用廣告'}
            </Button>
          )}
          {ad.status === 'active' && (
            <Button variant="secondary" onClick={handlePause}>暫停投放</Button>
          )}
          {ad.status === 'paused' && (
            <Button onClick={handleResume}>恢復投放</Button>
          )}
        </div>
      </div>

      {/* 投放數據 */}
      <div className="detail-section">
        <div className="detail-section__title">投放數據</div>
        <AdReport adId={id} />
      </div>

      {/* 基本資訊 */}
      <div className="detail-section">
        <div className="detail-section__title">基本資訊</div>
        <div className="detail-grid">
          <div>
            <div className="detail-item__label">廣告 ID</div>
            <div className="detail-item__value">{ad.id}</div>
          </div>
          <div>
            <div className="detail-item__label">版位類型</div>
            <div className="detail-item__value">{ad.placement_type || '-'}</div>
          </div>
          <div>
            <div className="detail-item__label">總曝光數</div>
            <div className="detail-item__value">{ad.total_impressions ? ad.total_impressions.toLocaleString() : '-'}</div>
          </div>
          <div>
            <div className="detail-item__label">日曝光上限</div>
            <div className="detail-item__value">{ad.daily_impressions > 0 ? ad.daily_impressions.toLocaleString() : '無限制'}</div>
          </div>
          <div>
            <div className="detail-item__label">走期開始</div>
            <div className="detail-item__value">{formatDate(ad.start_time)}</div>
          </div>
          <div>
            <div className="detail-item__label">走期結束</div>
            <div className="detail-item__value">{formatDate(ad.end_time)}</div>
          </div>
        </div>
      </div>

      {/* Targeting 設定 */}
      <div className="detail-section">
        <div className="detail-section__title">Targeting 設定</div>
        {Object.keys(targeting).length === 0 ? (
          <p className="detail-empty">{ad.status === 'draft' ? '草稿尚未完成 Targeting 設定' : '尚未設定 Targeting'}</p>
        ) : (
          <div className="detail-grid">
            {(targeting.frequency_capping_total > 0 || targeting.frequency_capping_daily > 0) && (
              <>
                <div>
                  <div className="detail-item__label">總曝光上限/人</div>
                  <div className="detail-item__value">{targeting.frequency_capping_total || '無限制'}</div>
                </div>
                <div>
                  <div className="detail-item__label">日曝光上限/人</div>
                  <div className="detail-item__value">{targeting.frequency_capping_daily || '無限制'}</div>
                </div>
              </>
            )}
            {targeting.day_parts_switch && targeting.day_parts?.length > 0 && (
              <div className="detail-item--full">
                <div className="detail-item__label">時段鎖定</div>
                <div className="detail-item__value">{targeting.day_parts.join(', ')}</div>
              </div>
            )}
            {targeting.include?.length > 0 && (
              <div>
                <div className="detail-item__label">包含關鍵字</div>
                <div className="detail-item__value">{targeting.include.join(', ')}</div>
              </div>
            )}
            {targeting.exclude?.length > 0 && (
              <div>
                <div className="detail-item__label">排除關鍵字</div>
                <div className="detail-item__value">{targeting.exclude.join(', ')}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 素材資訊 */}
      <div className="detail-section">
        <div className="detail-section__title">素材</div>
        {ad.creative_uuid ? (
          <div className="detail-grid">
            <div>
              <div className="detail-item__label">Creative UUID</div>
              <div className="detail-item__value mono">{ad.creative_uuid}</div>
            </div>
            <div>
              <div className="detail-item__label">Template UUID</div>
              <div className="detail-item__value mono">{ad.template_uuid || '-'}</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <p className="detail-empty">
              {ad.current_step >= 3 ? '尚未建立素材' : '請先完成廣告設定才能建立素材'}
            </p>
            {ad.current_step >= 3 && (
              <Button size="sm" onClick={() => navigate(`/ads/${ad.id}/creative`)}>建立素材</Button>
            )}
          </div>
        )}
      </div>


      {/* 廣告代碼 */}
      {ad.placement_uuid && (
        <PlacementCode ad={ad} />
      )}

      {/* PAC 資源 ID */}
      {ad.campaign_uuid && (
        <div className="detail-section detail-section--muted">
          <div className="detail-section__title">系統資訊</div>
          <div className="detail-grid">
            {ad.campaign_uuid && (
              <div>
                <div className="detail-item__label">Campaign UUID</div>
                <div className="detail-item__value mono">{ad.campaign_uuid}</div>
              </div>
            )}
            {ad.adgroup_uuid && (
              <div>
                <div className="detail-item__label">AdGroup UUID</div>
                <div className="detail-item__value mono">{ad.adgroup_uuid}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
